import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Lock,
  Plus,
  Copy,
  Printer,
  FilePdf,
  CheckCircle,
  Sparkle,
  Note,
  IdentificationCard,
} from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiError } from "../lib/api";
import { PUBLIC_FRONT_URL } from "../lib/publicFront";

/* ────────────────────────────────────────────────────────────────────
   /cc/admin/mints  ·  Curb Checkpoint Mint batch manager (admin only)

   Mint a batch of N pre-printed sticker codes. Each QR encodes a URL
   like https://curbcrew.co.za/cc/mint/<CODE> — when a customer scans
   it they land on the binding page where they prove ownership of
   their Curb Crew card and upload a document.

   This page is the print master: select N, click Mint, get a clean
   sheet of QRs sized for sticker-roll output.
   ──────────────────────────────────────────────────────────────────── */

const FRONT_ROOT = PUBLIC_FRONT_URL;

export default function CheckpointMintsAdmin() {
  const [count, setCount] = useState(20);
  const [note, setNote] = useState("");
  const [branchId, setBranchId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [batch, setBatch] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pamphletBranch, setPamphletBranch] = useState("");
  const [pamphletCard, setPamphletCard] = useState("");
  const [pamphletBusy, setPamphletBusy] = useState("");
  const [pamphletErr, setPamphletErr] = useState("");

  async function mint(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    setBatch(null);
    try {
      const r = await api.post("/cc/mints/batch", {
        count: Number(count) || 0,
        note,
        branch_id: branchId.trim() || null,
      });
      setBatch(r.data);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  }

  async function copyAll() {
    if (!batch?.codes) return;
    const text = batch.codes.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // no-op
    }
  }

  // Fetch a PDF blob from the backend (auth token attached by axios).
  // Returns the blob URL — caller decides whether to download or print.
  async function fetchPdfBlobUrl(path) {
    const r = await api.get(path, { responseType: "blob" });
    return URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  }

  // Force a download (a <a download> click).
  function triggerDownload(blobUrl, filename) {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
  }

  // Open the PDF in a new tab and auto-fire the browser's print dialog
  // (which is also where the user picks "Save as PDF"). Mirrors the
  // /admin/print-stickers UX.
  //
  // Note: do NOT pass 'noopener,noreferrer' here — for blob: URLs that
  // strips the popup's access to the opener's blob registry on some
  // browsers and the new tab stays at about:blank. The blob URL is
  // already same-origin and short-lived, so opener access is fine.
  function triggerPrint(blobUrl) {
    const w = window.open(blobUrl, "_blank");
    if (!w) {
      // Pop-up blocked — fall back to a same-tab navigation so the user
      // can at least see the PDF and hit Cmd/Ctrl+P themselves.
      window.location.href = blobUrl;
      return;
    }
    // Wait for the PDF viewer to load, then fire print.
    const fire = () => {
      try { w.focus(); w.print(); } catch { /* PDF viewers ship their own toolbar */ }
    };
    w.addEventListener("load", fire);
    // Some PDF viewers don't dispatch 'load' reliably; nudge after a beat.
    setTimeout(fire, 1200);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  }

  async function handleMintPdf(mode) {
    if (!batch?.batch_id) return;
    try {
      const url = await fetchPdfBlobUrl(`/cc/mints/batch/${batch.batch_id}/pdf`);
      if (mode === "print") triggerPrint(url);
      else triggerDownload(url, `curb-checkpoint-${batch.batch_id}.pdf`);
    } catch (ex) {
      setErr(formatApiError(ex));
    }
  }

  async function handlePamphlet(kind, mode) {
    setPamphletErr("");
    setPamphletBusy(`${kind}-${mode}`);
    try {
      const path =
        kind === "personalised"
          ? `/cc/pamphlet/card/${encodeURIComponent(pamphletCard.trim().toUpperCase())}.pdf`
          : `/cc/pamphlet/generic.pdf${pamphletBranch.trim() ? `?branch_id=${encodeURIComponent(pamphletBranch.trim())}` : ""}`;
      const url = await fetchPdfBlobUrl(path);
      if (mode === "print") {
        triggerPrint(url);
      } else {
        triggerDownload(
          url,
          kind === "personalised"
            ? `curb-checkpoint-${pamphletCard.trim().toUpperCase()}.pdf`
            : "curb-checkpoint-pamphlet.pdf",
        );
      }
    } catch (ex) {
      setPamphletErr(formatApiError(ex));
    } finally {
      setPamphletBusy("");
    }
  }

  return (
    <div className="min-h-screen bg-neutral-100 text-neutral-900">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-grid { grid-template-columns: repeat(4, minmax(0, 1fr)) !important; gap: 6mm !important; }
          .print-cell { break-inside: avoid; page-break-inside: avoid; border: 1px solid #ccc !important; padding: 4mm !important; }
        }
      `}</style>

      {/* Header */}
      <header className="no-print bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/cc/admin"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/70 hover:text-white"
            data-testid="mints-back"
          >
            <ArrowLeft size={14} weight="bold" /> Checkpoint Admin
          </Link>
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase">
            <Sparkle size={14} weight="bold" /> MINT · BATCHES
          </div>
        </div>
      </header>

      <main className="no-print max-w-6xl mx-auto px-6 py-10">
        <div className="font-mono text-[11px] tracking-[0.4em] uppercase text-neutral-500">
          CURB CHECKPOINT · MINT FACTORY
        </div>
        <h1 className="font-display text-4xl sm:text-6xl leading-tight mt-3">
          Mint a batch of stickers.
        </h1>
        <p className="mt-4 text-sm text-neutral-700 leading-relaxed max-w-xl">
          Each batch generates pre-printed sticker codes ready for the
          sticker-roll printer. Stickers are unbound until a customer
          scans + binds to their Curb Crew card at the till.
        </p>

        <form
          onSubmit={mint}
          className="mt-10 bg-white border-2 border-black p-6 grid sm:grid-cols-4 gap-4 items-end"
          data-testid="mints-form"
        >
          <label className="block">
            <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">
              Batch size
            </div>
            <input
              type="number"
              min={1}
              max={500}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              className="mt-1 w-full border-b-2 border-neutral-300 bg-transparent py-2 outline-none text-2xl font-display focus:border-black"
              data-testid="mints-count"
              required
            />
            <div className="text-[11px] text-neutral-400 mt-1">
              Max 500 per call
            </div>
          </label>
          <label className="block">
            <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">
              Branch ID (audit)
            </div>
            <input
              type="text"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value.toLowerCase().replace(/\s+/g, "-"))}
              placeholder="postnet-strand"
              className="mt-1 w-full border-b-2 border-neutral-300 bg-transparent py-2 outline-none text-sm font-mono focus:border-black"
              data-testid="mints-branch"
            />
            <div className="text-[11px] text-neutral-400 mt-1">
              Stamped onto every mint in this batch
            </div>
          </label>
          <label className="block">
            <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">
              Note (optional)
            </div>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. roll 1"
              className="mt-1 w-full border-b-2 border-neutral-300 bg-transparent py-2 outline-none text-sm focus:border-black"
              data-testid="mints-note"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="bg-black text-white font-mono text-[11px] tracking-[0.3em] uppercase py-4 disabled:opacity-40 inline-flex items-center justify-center gap-2"
            data-testid="mints-submit"
          >
            <Plus size={14} weight="bold" />
            {busy ? "Minting…" : "Mint batch"}
          </button>
        </form>

        {err && (
          <div
            className="mt-6 bg-red-50 border-2 border-red-600 text-red-800 p-4 text-sm"
            data-testid="mints-error"
          >
            {err}
          </div>
        )}

        {batch && (
          <div className="mt-10" data-testid="mints-result">
            <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
              <div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-neutral-500">
                  Batch
                </div>
                <div className="font-display text-3xl mt-1">
                  {batch.batch_id} ·{" "}
                  <span className="text-neutral-500">{batch.count} codes</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={copyAll}
                  className="border-2 border-black px-4 py-2 font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2"
                  data-testid="mints-copy-all"
                >
                  {copied ? (
                    <>
                      <CheckCircle size={12} weight="bold" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy size={12} weight="bold" /> Copy all
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleMintPdf("download")}
                  className="bg-black text-white px-4 py-2 font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2"
                  data-testid="mints-download-pdf"
                  title="Print-ready A4 sticker sheet — 21 stickers per page"
                >
                  <FilePdf size={12} weight="bold" /> Save PDF
                </button>
                <button
                  onClick={() => handleMintPdf("print")}
                  className="bg-[#E30613] text-white px-4 py-2 font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2"
                  data-testid="mints-print-pdf"
                  title="Opens the PDF in a new tab and triggers the print dialog. Pick 'Save as PDF' there too if you want."
                >
                  <Printer size={12} weight="bold" /> Print / Save PDF
                </button>
              </div>
            </div>
            <MintGrid codes={batch.codes} />
          </div>
        )}

        {/* ── Pamphlets section ─────────────────────────────────────── */}
        <section className="mt-20 border-t-2 border-black pt-10" data-testid="pamphlet-section">
          <div className="font-mono text-[11px] tracking-[0.4em] uppercase text-neutral-500">
            CURB CHECKPOINT · TAKE-HOME PAMPHLETS
          </div>
          <h2 className="font-display text-3xl sm:text-5xl leading-tight mt-3">
            Hand the customer a leaflet.
          </h2>
          <p className="mt-4 text-sm text-neutral-700 leading-relaxed max-w-xl">
            For walk-ins who don&apos;t have time to activate at the counter.
            A4 tri-fold, Postnet red. Print on plain paper, fold twice,
            done.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            {/* Generic */}
            <div
              className="bg-white border-2 border-black p-6 flex flex-col"
              data-testid="pamphlet-generic-card"
            >
              <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-neutral-500">
                <Note size={14} weight="bold" /> GENERIC STACK
              </div>
              <h3 className="font-display text-2xl mt-2">Counter stack</h3>
              <p className="mt-2 text-sm text-neutral-600 leading-relaxed flex-1">
                Same leaflet, for everyone. Optional branch ID stamps onto
                the back panel so customers know where they got it.
              </p>
              <label className="block mt-4">
                <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">
                  Branch ID (optional)
                </div>
                <input
                  type="text"
                  value={pamphletBranch}
                  onChange={(e) => setPamphletBranch(e.target.value.toLowerCase().replace(/\s+/g, "-"))}
                  placeholder="postnet-strand"
                  className="mt-1 w-full border-b-2 border-neutral-300 bg-transparent py-2 outline-none text-sm font-mono focus:border-black"
                  data-testid="pamphlet-generic-branch"
                />
              </label>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  onClick={() => handlePamphlet("generic", "download")}
                  disabled={pamphletBusy === "generic-download"}
                  className="bg-black text-white font-mono text-[10px] tracking-[0.2em] uppercase py-3 disabled:opacity-40 inline-flex items-center justify-center gap-2"
                  data-testid="pamphlet-generic-download"
                >
                  <FilePdf size={12} weight="bold" />
                  {pamphletBusy === "generic-download" ? "…" : "Save PDF"}
                </button>
                <button
                  onClick={() => handlePamphlet("generic", "print")}
                  disabled={pamphletBusy === "generic-print"}
                  className="bg-[#E30613] text-white font-mono text-[10px] tracking-[0.2em] uppercase py-3 disabled:opacity-40 inline-flex items-center justify-center gap-2"
                  data-testid="pamphlet-generic-print"
                  title="Opens the PDF in a new tab and triggers the print dialog. You can also pick 'Save as PDF' there."
                >
                  <Printer size={12} weight="bold" />
                  {pamphletBusy === "generic-print" ? "…" : "Print / Save"}
                </button>
              </div>
              <div className="mt-2 text-[10px] text-neutral-500 leading-snug">
                <strong>Tip:</strong> Print → in the print dialog, set
                Destination to <em>&quot;Save as PDF&quot;</em> if you want to
                email the file.
              </div>
            </div>

            {/* Personalised */}
            <div
              className="bg-black text-white p-6 flex flex-col"
              data-testid="pamphlet-personalised-card"
            >
              <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/60">
                <IdentificationCard size={14} weight="bold" /> PERSONALISED
              </div>
              <h3 className="font-display text-2xl mt-2">For one customer</h3>
              <p className="mt-2 text-sm text-white/70 leading-relaxed flex-1">
                Prints the customer&apos;s card code + an activation QR on the
                inside flap. They walk home, scan, set a password — done.
              </p>
              <label className="block mt-4">
                <div className="font-mono text-[9px] tracking-widest uppercase text-white/60">
                  Card code
                </div>
                <input
                  type="text"
                  value={pamphletCard}
                  onChange={(e) => setPamphletCard(e.target.value.toUpperCase().replace(/\s+/g, ""))}
                  placeholder="2RL6R7QU"
                  className="mt-1 w-full border-b-2 border-white/30 bg-transparent py-2 outline-none text-lg font-mono tracking-widest focus:border-white text-white"
                  data-testid="pamphlet-personalised-card-input"
                />
              </label>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  onClick={() => handlePamphlet("personalised", "download")}
                  disabled={pamphletBusy === "personalised-download" || !pamphletCard.trim()}
                  className="bg-white text-black font-mono text-[10px] tracking-[0.2em] uppercase py-3 disabled:opacity-40 inline-flex items-center justify-center gap-2"
                  data-testid="pamphlet-personalised-download"
                >
                  <FilePdf size={12} weight="bold" />
                  {pamphletBusy === "personalised-download" ? "…" : "Save PDF"}
                </button>
                <button
                  onClick={() => handlePamphlet("personalised", "print")}
                  disabled={pamphletBusy === "personalised-print" || !pamphletCard.trim()}
                  className="bg-[#E30613] text-white font-mono text-[10px] tracking-[0.2em] uppercase py-3 disabled:opacity-40 inline-flex items-center justify-center gap-2"
                  data-testid="pamphlet-personalised-print"
                  title="Opens the PDF in a new tab and triggers the print dialog."
                >
                  <Printer size={12} weight="bold" />
                  {pamphletBusy === "personalised-print" ? "…" : "Print / Save"}
                </button>
              </div>
            </div>
          </div>

          {pamphletErr && (
            <div
              className="mt-6 bg-red-50 border-2 border-red-600 text-red-800 p-4 text-sm"
              data-testid="pamphlet-error"
            >
              {pamphletErr}
            </div>
          )}
        </section>
      </main>

      {/* Print-only sheet — same data, optimised for paper */}
      {batch && (
        <div className="hidden print:block p-6">
          <h1 className="font-display text-2xl">
            Curb Checkpoint Mint · {batch.batch_id}
          </h1>
          <div className="font-mono text-xs mt-1 text-neutral-500">
            {batch.count} stickers · cut along borders
          </div>
          <div className="print-grid grid grid-cols-4 gap-3 mt-4">
            {batch.codes.map((c) => (
              <MintCell key={c} code={c} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MintGrid({ codes }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
      {codes.map((c) => (
        <MintCell key={c} code={c} />
      ))}
    </div>
  );
}

function MintCell({ code }) {
  const url = `${FRONT_ROOT}/cc/mint/${code}`;
  return (
    <div
      className="print-cell bg-white border-2 border-black p-4 flex flex-col items-center"
      data-testid={`mint-cell-${code}`}
    >
      <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-500">
        CURB CHECKPOINT
      </div>
      <QRCodeSVG value={url} size={120} level="M" includeMargin={false} className="mt-2" />
      <div className="mt-3 font-mono text-[10px] text-center break-all">
        {code}
      </div>
      <div className="font-mono text-[8px] text-neutral-400 mt-1">scan to bind</div>
    </div>
  );
}
